import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { CreditCard, CheckCircle, XCircle, Loader2, User, Shield, Package } from 'lucide-react'

const API_BASE = '/api'

// Textos en el idioma del cliente (lo devuelve el backend en fee.language)
const TEXTS: Record<string, Record<string, string>> = {
  es: {
    title_pickup: 'Pago de la recogida', title_reshipment: 'Pago del reenvío',
    desc_pickup: 'Recogida MRW a domicilio para tu solicitud de garantía. Si la garantía procede, te devolveremos este importe.',
    desc_pickup_norefund: 'Recogida MRW a domicilio para tu solicitud de garantía. Este importe cubre el servicio de recogida y no se devuelve; si la garantía procede, te enviamos la sustitución con los portes pagados.',
    desc_reshipment: 'Reenvío del producto de tu solicitud de garantía no procedente. Te lo enviaremos en 24-48 h tras el pago.',
    amount: 'Importe a pagar', vat: 'IVA incluido', pay: 'Pagar con tarjeta', secure: 'Pago seguro procesado por',
    loading: 'Cargando datos de pago...', error: 'Error', invalid: 'Enlace de pago no válido',
    ok_title: 'Pago realizado', ok_desc: 'El pago de tu solicitud {code} se ha procesado correctamente.',
    ok_next_pickup: 'Programaremos la recogida MRW y te enviaremos la etiqueta por email.',
    ok_next: 'Recibirás un email de confirmación con los siguientes pasos.',
    ko_title: 'Pago no completado', ko_desc: 'El pago de tu solicitud {code} no se ha podido procesar.',
    ko_next: 'Puedes intentarlo de nuevo desde el enlace del email o contactarnos si necesitas ayuda.',
    track: 'Ver estado de mi solicitud',
  },
  pt: {
    title_pickup: 'Pagamento da recolha', title_reshipment: 'Pagamento do reenvio',
    desc_pickup: 'Recolha MRW ao domicílio para o seu pedido de garantia. Se a garantia proceder, devolvemos-lhe este valor.',
    desc_pickup_norefund: 'Recolha MRW ao domicílio para o seu pedido de garantia. Este valor cobre o serviço de recolha e não é devolvido; se a garantia proceder, enviamos-lhe a substituição com os portes pagos.',
    desc_reshipment: 'Reenvio do produto do seu pedido de garantia não procedente. Enviamos-lho em 24-48 h após o pagamento.',
    amount: 'Valor a pagar', vat: 'IVA incluído', pay: 'Pagar com cartão', secure: 'Pagamento seguro processado por',
    loading: 'A carregar dados de pagamento...', error: 'Erro', invalid: 'Link de pagamento inválido',
    ok_title: 'Pagamento efetuado', ok_desc: 'O pagamento do seu pedido {code} foi processado com sucesso.',
    ok_next_pickup: 'Agendaremos a recolha MRW e enviaremos a etiqueta por email.',
    ok_next: 'Receberá um email de confirmação com os próximos passos.',
    ko_title: 'Pagamento não concluído', ko_desc: 'O pagamento do seu pedido {code} não pôde ser processado.',
    ko_next: 'Pode tentar novamente a partir do link do email ou contactar-nos se precisar de ajuda.',
    track: 'Ver estado do meu pedido',
  },
  en: {
    title_pickup: 'Pickup payment', title_reshipment: 'Return shipping payment',
    desc_pickup: 'MRW home pickup for your warranty claim. If the warranty is confirmed, we will refund this amount.',
    desc_pickup_norefund: 'MRW home pickup for your warranty claim. This amount covers the pickup service and is not refunded; if the warranty is confirmed, we ship the replacement with shipping paid.',
    desc_reshipment: 'Return shipping of the product from your unconfirmed warranty claim. We will ship it within 24-48 h after payment.',
    amount: 'Amount to pay', vat: 'VAT included', pay: 'Pay by card', secure: 'Secure payment processed by',
    loading: 'Loading payment data...', error: 'Error', invalid: 'Invalid payment link',
    ok_title: 'Payment completed', ok_desc: 'The payment for your claim {code} has been processed successfully.',
    ok_next_pickup: 'We will schedule the MRW pickup and email you the label.',
    ok_next: 'You will receive a confirmation email with the next steps.',
    ko_title: 'Payment not completed', ko_desc: 'The payment for your claim {code} could not be processed.',
    ko_next: 'You can try again from the link in the email or contact us if you need help.',
    track: 'View my claim status',
  },
}
const pickLang = () => {
  const l = (navigator.language || 'es').slice(0, 2)
  return TEXTS[l] ? l : 'es'
}

export default function ReturnPaymentPage() {
  const { code, feeId, token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState<any>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const lang = formData?.fee?.language && TEXTS[formData.fee.language] ? formData.fee.language : pickLang()
  const T = TEXTS[lang]

  useEffect(() => {
    document.title = `Pago ${code || ''} | JVS Informática`
    let m = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
    if (!m) { m = document.createElement('meta'); m.name = 'robots'; document.head.appendChild(m) }
    m.content = 'noindex'
  }, [code])

  useEffect(() => {
    if (!code || !feeId || !token) {
      setError(TEXTS[pickLang()].invalid)
      setLoading(false)
      return
    }
    fetch(`${API_BASE}/returns/pay/${code}/${feeId}/${token}`)
      .then(async res => {
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.detail || 'Error')
        if (!data.success) throw new Error(data.error || 'Error')
        setFormData(data)
      })
      .catch(err => setError(err.message || 'Error'))
      .finally(() => setLoading(false))
  }, [code, feeId, token])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-slate-600">{T.loading}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">{T.error}</h1>
          <p className="text-slate-600">{error}</p>
          <a href={`/returns/track/${code}`} className="inline-block mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5 px-6 rounded-xl">{T.track}</a>
        </div>
      </div>
    )
  }

  const fee = formData?.fee
  const amount = fee?.amount ? Number(fee.amount).toFixed(2).replace('.', ',') : ''
  const isPickup = fee?.kind === 'pickup'
  // El backend marca refundable=true solo cuando la recogida se devuelve si la
  // garantía procede (consumidores). A distribuidores no se les devuelve.
  const pickupRefundable = fee?.refundable !== false

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-6 py-5 text-center">
          <img src="/jvs-logo.jpg" alt="JVS Informática" className="h-10 mx-auto mb-3 rounded" />
          <h1 className="text-lg font-bold text-white">{isPickup ? T.title_pickup : T.title_reshipment}</h1>
          <p className="text-amber-100 text-sm mt-0.5">{code}</p>
        </div>

        <div className="p-6">
          {fee && (
            <div className="bg-slate-50 rounded-xl p-4 mb-5 space-y-2.5">
              {fee.customer_name && (
                <div className="flex items-center gap-2.5">
                  <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-700 font-medium">{fee.customer_name}</span>
                </div>
              )}
              <div className="flex items-start gap-2.5">
                <Package className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">{isPickup ? (pickupRefundable ? T.desc_pickup : T.desc_pickup_norefund) : T.desc_reshipment}</span>
              </div>
            </div>
          )}

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-5 text-center">
            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider mb-1">{T.amount}</p>
            <p className="text-4xl font-extrabold text-slate-900">{amount} <span className="text-lg font-bold text-slate-500">€</span></p>
            <p className="text-xs text-slate-500 mt-1">{T.vat}</p>
          </div>

          {formData && (
            <form ref={formRef} action={formData.redsys_url} method="POST">
              <input type="hidden" name="Ds_SignatureVersion" value={formData.Ds_SignatureVersion} />
              <input type="hidden" name="Ds_MerchantParameters" value={formData.Ds_MerchantParameters} />
              <input type="hidden" name="Ds_Signature" value={formData.Ds_Signature} />
              <button type="button" onClick={() => formRef.current?.submit()}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-md shadow-emerald-200">
                <CreditCard className="w-5 h-5" />
                {T.pay}
              </button>
            </form>
          )}

          <div className="flex items-center justify-center gap-2 mt-4 py-3 border-t border-slate-100">
            <Shield className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-400">{T.secure}</span>
            <img src="https://sis.redsys.es/sis/img/redsys.png" alt="Redsys" className="h-4" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
          </div>
        </div>
      </div>
    </div>
  )
}

export function ReturnPaymentOkPage() {
  const { code } = useParams()
  const T = TEXTS[pickLang()]
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">{T.ok_title}</h1>
        <p className="text-slate-600 mb-4">{T.ok_desc.replace('{code}', code || '')}</p>
        <p className="text-sm text-slate-500">{T.ok_next}</p>
        <a href={`/returns/track/${code}`}
          className="inline-block mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5 px-6 rounded-xl transition-colors">
          {T.track}
        </a>
      </div>
    </div>
  )
}

export function ReturnPaymentKoPage() {
  const { code } = useParams()
  const T = TEXTS[pickLang()]
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">{T.ko_title}</h1>
        <p className="text-slate-600 mb-4">{T.ko_desc.replace('{code}', code || '')}</p>
        <p className="text-sm text-slate-500 mb-6">{T.ko_next}</p>
        <a href={`/returns/track/${code}`}
          className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5 px-6 rounded-xl transition-colors">
          {T.track}
        </a>
      </div>
    </div>
  )
}
